import type { FunctionComponent } from 'react'
import { useState } from 'react'
import CopyIcon from './icons/CopyIcon'
import { useTranslation } from '../../contexts/localization/Localization'

interface CopyToClipboardProps {
  text: string | (() => string)
  richText?: string | (() => string)
  className: string
}

/**
 * A button that copies the provided text to the clipboard when clicked.
 * @story User Story 8109: Productize AI-supported sections
 */
const CopyToClipboardButton: FunctionComponent<CopyToClipboardProps> = ({ text, richText, className }) => {
  const { text: t } = useTranslation()

  // State
  const [isCopied, setIsCopied] = useState(false)

  return (
    <span
      className={`${className} copy-btn cursor-pointer inline-block ${
        isCopied
          ? 'text-[#41bb5b] hover:text-[#41bb5b] dark:text-[#65da8d] dark:hover:text-[#65da8d]'
          : 'text-[#00000044] hover:text-[#00000099] dark:text-[#FFFFFF66] dark:hover:text-[#FFFFFF99]'
      }`}
      onClick={async () => {
        const textValue = typeof text === 'function' ? text() : text
        const richTextValue = typeof richText === 'function' ? richText() : richText
        if (richTextValue) {
          // Copy both plain text and rich text into the clipboard
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': new Blob([textValue], { type: 'text/plain' }),
              'text/html': new Blob([richTextValue], { type: 'text/html' }),
            }),
          ])
        } else {
          // Copy only plain text into the clipboard
          await navigator.clipboard.writeText(textValue)
        }
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
      }}
      title={t('copyToClipboard')}
    >
      <CopyIcon />
    </span>
  )
}

export default CopyToClipboardButton
